import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack,
  Divider,
  Alert,
  useTheme,
  alpha,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from '@mui/material';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import { UserRole, MembershipTier, DesignerTier, HomeownerTier, useImpersonation } from '../../contexts/ImpersonationContext';
import { useNotification } from '../../contexts/NotificationContext';

interface RoleSelectorProps {
  open: boolean;
  onClose: () => void;
}

interface RoleOption {
  role: UserRole;
  icon: React.ReactNode;
  color: string;
  description: string;
}

interface TierOption {
  tier: string;
  label: string;
  features: string[];
  lockedFeatures?: string[];
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const { startImpersonation, actualRole } = useImpersonation();
  const { showNotification } = useNotification();
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [selectedTier, setSelectedTier] = useState<MembershipTier>(null);
  const [showTierSelection, setShowTierSelection] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const roleOptions: RoleOption[] = [
    {
      role: 'Designer',
      icon: <DesignServicesIcon sx={{ fontSize: 40 }} />,
      color: '#d22268',
      description: 'View the application with different designer membership tiers',
    },
    {
      role: 'Homeowner',
      icon: <HomeIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      description: 'Experience the application with different homeowner subscription levels',
    },
  ];

  const designerTiers: TierOption[] = [
    {
      tier: 'Home',
      label: 'Home Membership',
      features: [
        'HDG Seal of Approval',
        'Regional Advertising/Marketing',
        'Smart-Guild Matching',
        'Designer Hub Dashboard',
      ],
      lockedFeatures: [
        'Design Hub: Reporting',
        'Homeowner Financing',
        'Designer Referral Program',
      ],
    },
    {
      tier: 'Design',
      label: 'Design Membership',
      features: [
        'All Home features',
        'Design Hub: Reporting',
      ],
      lockedFeatures: [
        'Homeowner Financing',
        'Designer Referral Program',
      ],
    },
    {
      tier: 'Design+',
      label: 'Design+ Membership',
      features: [
        'All Design features',
        'Homeowner Financing',
        'Designer Referral Program',
        'Full feature access',
      ],
      lockedFeatures: [],
    },
  ];

  const homeownerTiers: TierOption[] = [
    {
      tier: 'Basic',
      label: 'Basic (Free)',
      features: [
        'Limited Project Gallery',
        'Limited Guild Guide Articles',
      ],
      lockedFeatures: [
        'Curated Project Gallery',
        'Guild Guide Articles',
        'Powerful Filters',
        'Create & Save Idea Books',
        'Smart-Guild Matching',
        'Education',
      ],
    },
    {
      tier: 'Guild',
      label: 'Guild (Monthly)',
      features: [
        'Curated Project Gallery',
        'Guild Guide Articles',
        'Powerful Filters',
        'Create & Save Idea Books',
        'Smart-Guild Matching',
        'Education',
        'Full feature access',
      ],
      lockedFeatures: [],
    },
    {
      tier: 'Guild+',
      label: 'Guild+ (Annually)',
      features: [
        'All Guild features',
        'Annual billing discount',
        'Save 17% compared to monthly',
        'Full feature access',
      ],
      lockedFeatures: [],
    },
  ];

  const availableRoles = roleOptions.filter((option) => option.role !== actualRole);

  const getTierOptions = (): TierOption[] => {
    return selectedRole === 'Designer' ? designerTiers : homeownerTiers;
  };

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setShowTierSelection(true);
    // Set default tier
    setSelectedTier(role === 'Designer' ? 'Home' : 'Basic');
  };

  const handleTierSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTier(event.target.value as MembershipTier);
  };

  const handleTierConfirm = () => {
    setShowConfirmation(true);
    setShowTierSelection(false);
  };

  const handleConfirm = () => {
    if (selectedRole && selectedTier) {
      startImpersonation(selectedRole, selectedTier);
      showNotification({
        type: 'info',
        message: `Now viewing as ${selectedRole} - ${selectedTier}`,
        animation: 'slideTop',
      });
      handleReset();
      onClose();
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleBack = () => {
    setShowTierSelection(false);
    setSelectedTier(null);
  };

  const handleReset = () => {
    setSelectedRole(null);
    setSelectedTier(null);
    setShowTierSelection(false);
    setShowConfirmation(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const getRoleOption = () => roleOptions.find((r) => r.role === selectedRole);
  const roleOption = getRoleOption();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '500px',
        },
      }}
    >
      {/* Step 1: Role Selection */}
      {!showTierSelection && !showConfirmation && (
        <>
          <DialogTitle>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AdminPanelSettingsIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h5" fontWeight={600}>
                  Select Role to Impersonate
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Choose a role and membership tier to test different user experiences
              </Typography>
            </Stack>
          </DialogTitle>

          <DialogContent>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              <Typography variant="body2" fontWeight={500}>
                You'll see the interface and features available to this role, but your admin privileges remain active.
              </Typography>
            </Alert>

            <Stack spacing={2}>
              {availableRoles.map((option) => (
                <Card
                  key={option.role}
                  elevation={0}
                  sx={{
                    border: `2px solid ${theme.palette.grey[200]}`,
                    borderRadius: 3,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: option.color,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(option.color, 0.2)}`,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleRoleSelect(option.role)}
                    sx={{ p: 2 }}
                  >
                    <CardContent>
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            backgroundColor: alpha(option.color, 0.1),
                            color: option.color,
                          }}
                        >
                          {option.icon}
                        </Box>

                        <Stack spacing={1} flex={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {option.role}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </DialogActions>
        </>
      )}

      {/* Step 2: Tier Selection */}
      {showTierSelection && !showConfirmation && roleOption && (
        <>
          <DialogTitle>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ color: roleOption.color }}>{roleOption.icon}</Box>
                <Typography variant="h5" fontWeight={600}>
                  Select {selectedRole} Membership Tier
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Choose a membership tier to test feature access
              </Typography>
            </Stack>
          </DialogTitle>

          <DialogContent>
            <RadioGroup value={selectedTier} onChange={handleTierSelect}>
              <Stack spacing={2}>
                {getTierOptions().map((tierOption) => (
                  <Card
                    key={tierOption.tier}
                    elevation={0}
                    sx={{
                      border: `2px solid ${
                        selectedTier === tierOption.tier
                          ? roleOption.color
                          : theme.palette.grey[200]
                      }`,
                      borderRadius: 3,
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor:
                        selectedTier === tierOption.tier
                          ? alpha(roleOption.color, 0.05)
                          : 'transparent',
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelectedTier(tierOption.tier as MembershipTier)}
                      sx={{ p: 2 }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <FormControlLabel
                                value={tierOption.tier}
                                control={<Radio />}
                                label=""
                                sx={{ m: 0 }}
                              />
                              <Typography variant="h6" fontWeight={600}>
                                {tierOption.label}
                              </Typography>
                            </Stack>
                            {tierOption.lockedFeatures && tierOption.lockedFeatures.length === 0 && (
                              <Chip
                                label="Full Access"
                                size="small"
                                sx={{
                                  backgroundColor: roleOption.color,
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Stack>

                          <Divider />

                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              ✓ Available Features:
                            </Typography>
                            {tierOption.features.map((feature, index) => (
                              <Typography
                                key={index}
                                variant="caption"
                                color="text.secondary"
                                sx={{ pl: 2 }}
                              >
                                • {feature}
                              </Typography>
                            ))}
                          </Stack>

                          {tierOption.lockedFeatures && tierOption.lockedFeatures.length > 0 && (
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="error" fontWeight={600}>
                                <LockIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                Locked Features:
                              </Typography>
                              {tierOption.lockedFeatures.map((feature, index) => (
                                <Typography
                                  key={index}
                                  variant="caption"
                                  color="error"
                                  sx={{ pl: 2 }}
                                >
                                  • {feature}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </RadioGroup>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleBack} sx={{ textTransform: 'none' }}>
              Back
            </Button>
            <Button
              onClick={handleTierConfirm}
              variant="contained"
              disabled={!selectedTier}
              sx={{
                textTransform: 'none',
                backgroundColor: roleOption.color,
                '&:hover': {
                  backgroundColor: alpha(roleOption.color, 0.8),
                },
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {/* Step 3: Confirmation */}
      {showConfirmation && roleOption && (
        <>
          <DialogTitle>
            <Typography variant="h6" fontWeight={600}>
              Confirm Role Impersonation
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Stack spacing={2}>
                <Typography variant="body1">
                  You are about to view the application as:
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={roleOption.icon as any}
                    label={`${selectedRole} - ${selectedTier}`}
                    sx={{
                      backgroundColor: roleOption.color,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '1rem',
                      height: 36,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  • A persistent banner will appear at the top of all pages
                  <br />
                  • You'll see only features available to this tier
                  <br />
                  • Your admin privileges remain active for security
                  <br />
                  • This session will be logged for audit purposes
                  <br />
                  • Click "Return to Admin View" in the banner to exit
                </Typography>
              </Stack>
            </Alert>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleCancel} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{
                textTransform: 'none',
                backgroundColor: roleOption.color,
                '&:hover': {
                  backgroundColor: alpha(roleOption.color, 0.8),
                },
              }}
            >
              Start Impersonation
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
